import React, { useEffect } from 'react';
import { Button, Icons, ProgressPolygonList } from '@silverfort/sf-components';
import { SoftwareStatus } from '../../types';
import { Banner } from '../../components/Banner';
import { httpPost } from '../../../../utils';

const DownloadStep: React.FC<{ softwareStatus: SoftwareStatus }> = ({ softwareStatus }) => {
    const [downloadingProgress, setDownloadingProgress] = React.useState<number>(softwareStatus.downloadingProgress);
    const [isDownloading, setIsDownloading] = React.useState<boolean>(softwareStatus.downloading);
    const [downloadCompleted, setDownloadCompleted] = React.useState<boolean>(
        softwareStatus.lastDownloadedVersion ===
            (softwareStatus.versionUpgradeInProgress || softwareStatus.maxTowerVersion) && !softwareStatus.downloading
    );

    const startDownloading = () => async () => {
        setIsDownloading(true);
        setDownloadingProgress(0);

        try {
            await httpPost(`/softwareUpdates/downloadLatest`);
        } catch (error) {
            console.error('Error starting download:', error);
            setIsDownloading(false);
            setDownloadingProgress(0);
        }
    };

    useEffect(() => {
        if (softwareStatus.downloadError) {
            setIsDownloading(false);
            setDownloadingProgress(0);
            setDownloadCompleted(false);
            console.log('Download error:', softwareStatus.downloadError);
        } else {
            setIsDownloading(softwareStatus.downloading);
            setDownloadingProgress(softwareStatus.downloadingProgress);
            setDownloadCompleted(
                softwareStatus.lastDownloadedVersion === softwareStatus.maxTowerVersion && !softwareStatus.downloading
            );
        }

        if (softwareStatus.offlineImageUploaded) {
            setIsDownloading(false);
            setDownloadCompleted(true);
        }
    }, [softwareStatus]);

    return (
        <>
            {downloadCompleted && (
                <Banner theme="Success">
                    <Banner.Body
                        content={
                            softwareStatus.offlineImageUploaded
                                ? 'The package has been imported from the Silverfort Command Line Interface.'
                                : 'Download completed successfully'
                        }
                    />
                </Banner>
            )}
            {softwareStatus.downloadError && (
                <Banner theme="Critical">
                    <Banner.Body content={`Download new version failed, Please try again.`} />
                </Banner>
            )}
            <div className="p-4 w-full">
                <div className="flex flex-col gap-4">
                    <div className="text-sf-gray-800 font-sf-poppins text-18 font-medium">
                        Your software upgrade is ready for download
                    </div>
                    <div className="text-13 text-sf-gray-500 font-sf">
                        After downloading, you'll be able to begin the upgrade process.
                    </div>
                </div>
                <div className="h-8 mt-10">
                    {isDownloading && (
                        <div>
                            <ProgressPolygonList
                                isAnimationActive={false}
                                items={[
                                    {
                                        label: `${downloadingProgress}% Downloaded`,
                                        progress: downloadingProgress,
                                        endColor: '#E9F5FF',
                                        startColor: '#188DED'
                                    }
                                ]}
                            />
                        </div>
                    )}
                </div>
                <div className="mt-10 h-16 flex justify-end">
                    <div className="flex flex-col gap-2 items-end">
                        <Button
                            disabled={
                                isDownloading ||
                                softwareStatus.upgradingPushServices ||
                                softwareStatus.upgradingNodes ||
                                softwareStatus.upgradingTower
                            }
                            iconLeft={Icons.Download}
                            text={downloadCompleted ? 'Download Again' : 'Download'}
                            onClick={startDownloading()}
                        />
                        <div className="text-13 font-sf text-sf-gray-300">
                            Available version: {softwareStatus.maxTowerVersion}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DownloadStep;
